import React, { useState } from 'react';
import { nomineeLogin, requestNomineePassword } from '../utils/nomineeApi';
import { AlertTriangle, CheckCircle, Key, LoaderPinwheel, LogIn } from 'lucide-react';
import Loading from './elements/Loading';
import Button from './elements/Button';
import Input from './elements/Input';

const NomineeLoginStep01 = ({ next }) => {
  const [showResetRequest, setShowResetRequest] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    setIsLoading(true);

    try {
        const result = await nomineeLogin(email, password);
        if (result.success) {
          
          next(result);
        } else {
            setError(result.message || 'Login failed. Please check your credentials.');
        }
    } catch (e) {
      setError('A network error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <h2 className="text-2xl font-bold text-primary-dark text-center flex items-center justify-center font-sans">
        <LogIn className="mr-3 text-accent-main" />
        Nominee Login
      </h2>
      <p className="text-sm text-center text-text-muted font-sans">
        Access your nominee account.
      </p>

      {error && (
        <div className="flex items-center p-3 text-red-700 bg-red-100 rounded-lg">
          <AlertTriangle className="w-5 h-5 mr-2" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {successMessage && (
        <div className="flex items-center p-3 text-green-700 bg-green-100 rounded-lg">
          <CheckCircle className="w-5 h-5 mr-2" />
          <span className="text-sm">{successMessage}</span>
        </div>
      )}

      {!showResetRequest && (
        <form onSubmit={handleSubmit} className="space-y-4 font-sans">
          <Input
            id="email"
            label="Nominee Email Address"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            placeholder="you@example.com"
            disabled={isLoading}
            autoFocus
          />
          <div>
            <Input
              id="password"
              label="Nominee Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="••••••••"
              disabled={isLoading}
            />
          </div>
          <Button
            type="submit"
            className="w-full"
            disabled={isLoading}
            loading={isLoading}
          >
              <span className="flex items-center"><LogIn className="w-5 h-5 mr-2" /> Log In</span>
          </Button>
        </form>
      )}

      {showResetRequest && (
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            setIsLoading(true);
            setError(null);
            setSuccessMessage(null);
            try {
              const result = await requestNomineePassword(email);
              if (result.success) {
                setSuccessMessage(result.message || 'If your email exists, a password has been sent.');
              } else {
                setError(result.message || 'Failed to request password reset.');
              }
              setShowResetRequest(false);
            } catch (e) {
              setError('Failed to request password reset.');
            } finally {
              setIsLoading(false);
            }
          }}
          className="space-y-4 font-sans"
        >
          <Input
            id="resetEmail"
            label="Enter your email to get a new password"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            placeholder="you@example.com"
            disabled={isLoading}
          />
          <Button
            type="submit"
            className="w-full"
            disabled={isLoading}
          >
            Request New Password
          </Button>
        </form>
      )}

      <div className="text-center text-sm flex flex-col gap-2 font-sans">
        {!showResetRequest && (
          <Button
            type="anchor"
            onClick={() => {
              setShowResetRequest(true);
              setSuccessMessage(null);
              setError(null);
            }}
            disabled={isLoading}
          >
            Request password?
          </Button>
        )}
        {showResetRequest && (
          <Button
            type="anchor"
            onClick={() => {
              setShowResetRequest(false);
              setSuccessMessage(null);
              setError(null);
            }}
            disabled={isLoading}
          >
            Back to login
          </Button>
        )}
      </div>
    </>
  );
};

export default NomineeLoginStep01;
